package com.devi3east.bashbuddy.cart.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.devi3east.bashbuddy.cart.domain.models.CartFeeBreakdown
import com.devi3east.bashbuddy.cart.domain.models.CartItem
import com.devi3east.bashbuddy.cart.domain.repositories.CartRepository
import com.devi3east.bashbuddy.cart.results.CheckoutResult
import com.devi3east.bashbuddy.cart.storage.CartStorage
import com.devi3east.bashbuddy.features.common.util.UrlOpener
import com.devi3east.bashbuddy.features.events.common.data.models.Ticket
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.math.round

class CartViewModel(
    private val repository: CartRepository,
    private val tokenProvider: () -> String?,
    private val urlOpener: UrlOpener,
    private val cartStorage: CartStorage
) : ViewModel() {

    private val _uiState = MutableStateFlow(CartUiState())
    val uiState: StateFlow<CartUiState> = _uiState.asStateFlow()

    private val _uiEvent = MutableSharedFlow<CartUiEvent>()
    val uiEvent: SharedFlow<CartUiEvent> = _uiEvent.asSharedFlow()

    init {
        try {
            val persistedItems = cartStorage.load()
            _uiState.value = updateFees(
                CartUiState(items = persistedItems)
            )
        } catch (e: Exception) {
            println("❌ Failed to load cart: ${e.message}")
        }
    }

    fun checkout(
        orgId: String,
        jurisdictionEligibilityConfirmed: Boolean,
        onResult: (CheckoutResult) -> Unit,
        onError: (String) -> Unit
    ) {
        if (!jurisdictionEligibilityConfirmed) {
            val message =
                "Please confirm that you are legally eligible to purchase raffle tickets."

            _uiEvent.tryEmit(CartUiEvent.ShowMessage(message))
            onError(message)
            return
        }

        val token = tokenProvider() ?: run {
            val message = "You must be signed in to continue."
            _uiEvent.tryEmit(CartUiEvent.ShowMessage(message))
            onError(message)
            return
        }

        val itemsForOrg = _uiState.value.items.filter {
            it.ticket.createdById == orgId
        }

        if (itemsForOrg.isEmpty()) {
            val message = "No items in the cart for this organization."

            _uiEvent.tryEmit(CartUiEvent.ShowMessage(message))
            onError(message)
            return
        }

        viewModelScope.launch {
            _uiState.update {
                it.copy(
                    isLoading = true,
                    errorMessage = null
                )
            }

            try {
                when (
                    val result = repository.checkout(
                        token = token,
                        items = itemsForOrg,
                        jurisdictionEligibilityConfirmed =
                            jurisdictionEligibilityConfirmed
                    )
                ) {
                    is CheckoutResult.Success -> {
                        val subPurchase = result.response.subPurchases.find {
                            it.organizationId == orgId
                        }

                        if (subPurchase == null) {
                            val message =
                                "No checkout session found for organization $orgId."

                            _uiState.update {
                                it.copy(
                                    isLoading = false,
                                    errorMessage = message
                                )
                            }

                            onError(message)
                            return@launch
                        }

                        urlOpener.openUrl(subPurchase.sessionUrl)

                        _uiState.update {
                            it.copy(
                                isLoading = false,
                                successMessage = "Redirecting to checkout…"
                            )
                        }

                        onResult(result)
                    }

                    is CheckoutResult.InventoryError -> {
                        _uiState.update { state ->
                            val updatedItems = state.items
                                .map { item ->
                                    if (item.ticket._id == result.error.ticketId) {
                                        item.copy(
                                            quantity =
                                                result.error.availableQuantity ?: 0
                                        )
                                    } else {
                                        item
                                    }
                                }
                                .filter { it.quantity > 0 }

                            persist(updatedItems)

                            state.copy(
                                items = updatedItems,
                                isLoading = false,
                                inventoryErrorMessage =
                                    result.error.errorMessage
                            )
                        }

                        _uiEvent.tryEmit(
                            CartUiEvent.ShowMessage(
                                _uiState.value.inventoryErrorMessage
                                    ?: "Inventory updated."
                            )
                        )

                        onResult(result)
                    }

                    is CheckoutResult.Failure -> {
                        val message =
                            result.exception.message ?: "Checkout failed."

                        _uiState.update {
                            it.copy(
                                isLoading = false,
                                errorMessage = message
                            )
                        }

                        _uiEvent.tryEmit(
                            CartUiEvent.ShowMessage(message)
                        )

                        onError(message)
                    }

                    CheckoutResult.Cancelled -> {
                        _uiState.update {
                            it.copy(isLoading = false)
                        }

                        _uiEvent.tryEmit(
                            CartUiEvent.CheckoutCancelled
                        )

                        onResult(CheckoutResult.Cancelled)
                    }
                }
            } catch (e: Exception) {
                val message = e.message ?: "Unknown checkout error."

                _uiState.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = message
                    )
                }

                onError(message)
            }
        }
    }

    fun confirmPurchase(
        sessionId: String,
        orgId: String,
        onSuccess: () -> Unit,
        onFailure: (String) -> Unit
    ) {
        val token = tokenProvider() ?: run {
            onFailure("Not authenticated")
            return
        }
        viewModelScope.launch {
            try {
                val result = repository.confirmPurchase(token, sessionId)
                if (result.isSuccess) {
                    val beforeItems = _uiState.value.items
                    val remainingItems = beforeItems.filterNot {
                        it.ticket.createdById == orgId
                    }
                    cartStorage.save(remainingItems)
                    _uiState.update {
                        it.copy(items = remainingItems)
                    }
                    onSuccess()
                } else {
                    val msg = result.exceptionOrNull()?.message ?: "Confirm failed"
                    onFailure(msg)
                }
            } catch (e: Exception) {
                onFailure(e.message ?: "Confirm failed")
            }
        }
    }

    fun addToCart(ticket: Ticket, quantity: Int) {
        _uiState.update { state ->
            val updated = state.items.toMutableList()
            val existing = updated.find { it.ticket._id == ticket._id }

            if (existing != null) {
                updated[updated.indexOf(existing)] =
                    existing.copy(quantity = existing.quantity + quantity)
            } else {
                updated += CartItem(ticket, quantity)
            }
            val newState = state.copy(items = updated)
            persist(updated)

            updateFees(newState)
        }
    }

    fun removeFromCart(ticketId: String) {
        _uiState.update { state ->
            val updated = state.items.filterNot { it.ticket._id == ticketId }
            val newState = state.copy(items = updated)
            persist(updated)

            updateFees(newState)
        }
    }

    fun updateQuantity(ticketId: String, newQuantity: Int) {
        _uiState.update { state ->
            val updated = state.items.map {
                if (it.ticket._id == ticketId) it.copy(quantity = newQuantity) else it
            }.filter { it.quantity > 0 }
            val newState = state.copy(items = updated)
            persist(updated)

            updateFees(newState)
        }
    }

    fun removeItemsForOrganization(orgId: String) {
        _uiState.update { state ->
            val updated = state.items.filterNot { it.ticket.createdById == orgId }
            persist(updated)
            state.copy(items = updated)
        }
    }

    fun updateShipping(ticketId: String, selected: Boolean) {
        _uiState.update { state ->
            val updated = state.items.map {
                if (it.ticket._id == ticketId) it.copy(shippingSelected = selected)
                else it
            }
            val newState = state.copy(items = updated)
            persist(updated)

            updateFees(newState)
        }
    }

    fun clearCart(reason: String) {
        println("🧹 Clearing cart — reason=$reason")
        cartStorage.clear()
        _uiState.value = CartUiState(items = emptyList())
    }

    fun dismissInventoryError() {
        _uiState.update { it.copy(inventoryErrorMessage = null) }
    }

    /*private fun calculateCartFees(): CartFeeBreakdown {
        val items = _uiState.value.items

        //val totalQty = items.sumOf { it.quantity }

        val base = items.sumOf { it.ticket.donation * it.quantity }

        val shipping = items
            .filter { it.shippingSelected }
            .sumOf { it.ticket.shippingFee }

        // --- PLATFORM FEE ---
        val platformPercent = 0.05
        val platformMin = 60L

        val percentPlatformFee = round(base * platformPercent).toLong()
        val platformFee = max(percentPlatformFee, platformMin)

        // --- STRIPE ---
        val stripeFlat = 30L
        val stripePercentRate = 0.03

        val subtotalBeforeStripe =
            base + shipping + platformFee + stripeFlat

        val stripeFee =
            stripeFlat + round(subtotalBeforeStripe * stripePercentRate).toLong()

        val total = base + shipping + platformFee + stripeFee

        return CartFeeBreakdown(
            base = base,
            shipping = shipping,
            platformFee = platformFee,
            stripeFee = stripeFee,
            total = total
        )
    }*/

    private fun calculateCartFees(): CartFeeBreakdown {
        val items = _uiState.value.items

        val base = items.sumOf { it.ticket.donation * it.quantity }

        val shipping = items
            .filter { it.shippingSelected }
            .sumOf { it.ticket.shippingFee }

        // Amount the customer is buying before service fee
        val feeBase = base + shipping

        // --- SERVICE FEE CONFIG ---
        // Customer-facing bundled service fee = 3.9% + $0.50
        val servicePercent = 0.039
        val serviceFlat = 50L

        val serviceFee =
            serviceFlat + round(feeBase * servicePercent).toLong()

        // --- STRIPE ESTIMATE ---
        // Stripe is approximately 2.9% + $0.30
        val stripeFlat = 30L
        val stripePercentRate = 0.029

        val totalBeforeStripeBreakdown =
            base + shipping + serviceFee

        val stripeFee =
            stripeFlat + round(totalBeforeStripeBreakdown * stripePercentRate).toLong()

        // Platform keeps whatever remains from the bundled service fee
        val platformFee = serviceFee - stripeFee

        val total = base + shipping + serviceFee

        return CartFeeBreakdown(
            base = base,
            shipping = shipping,
            platformFee = platformFee,
            stripeFee = stripeFee,
            total = total
        )
    }

    private fun updateFees(state: CartUiState): CartUiState {
        val fees = calculateCartFees()
        return state.copy(fees = fees)
    }

    /*fun calculateCartTotalWithFees(): Long {
        val items = _uiState.value.items
        val base = items.sumOf { it.ticket.donation * it.quantity }
        val platformFee = 20L * items.sumOf { it.quantity }
        val stripeFlat = 30L
        val shipping = items
            .mapNotNull { it.ticket.createdById }
            .distinct()
            .sumOf { org ->
                items.firstOrNull { it.ticket.createdById == org }?.ticket?.shippingFee ?: 0L
            }
        val subtotal = base + platformFee + stripeFlat + shipping
        val stripePercent = (subtotal * 0.03).roundToLong()
        return subtotal + stripePercent
    }*/

    private fun persist(items: List<CartItem>) {
        try {
            cartStorage.save(items)
        } catch (e: Exception) {
            println("❌ Failed to persist cart: ${e.message}")
        }
    }
}